import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import {
  ICustomerInfoFieldsFragment,
  IRoleWithPermissionsFieldsFragment,
  IUserFieldsFragment,
} from '../apollo/graphqlSchema';
import { isNil } from 'lodash';
import moment from 'moment';
import { IAnalyticsEventBase } from './types';
import Syndicate from '../../pages/Auth/utils/Syndicate';
import { GraphQLDate } from '../types';
import { MaybeNil } from '../interfaces';
import { PAGE_VIEWED_EVENT } from './events/page/events';
import { IAnalyticsEventPage } from './events/page/types';
import { IS_SCREENSHOT_MODE } from '../../config/constants';

interface ICustomerAnalyticsInfo {
  org_name: string;
  org_region: string;
  warehouse_name: string;
  org_created_on_date: string;
  // 'connection_type': string; TODO: will be added later
}

interface IUserAnalyticsInfo {
  user_email: string;
  user_role: string[];
  first_login_date: MaybeNil<string>; // pending users don't have created date
}

enum AnalyticsState {
  NotAvailable = 'NOT_AVAILABLE',
  Public = 'PUBLIC',
  LoggedIn = 'LOGGED_IN',
}

type IPartialCustomerAnalyticsInfo = Pick<
  ICustomerAnalyticsInfo,
  'org_name' | 'org_region'
>;

type IAnalyticsState =
  | { state: AnalyticsState.NotAvailable }
  | {
      state: AnalyticsState.Public;
      browser: AnalyticsBrowser;
      partialCustomerAnalyticsInfo: IPartialCustomerAnalyticsInfo;
    }
  | {
      state: AnalyticsState.LoggedIn;
      browser: AnalyticsBrowser;
      customerAnalyticsInfo: ICustomerAnalyticsInfo;
      userAnalyticsInfo: IUserAnalyticsInfo;
    };

const toIsoDate = (date: GraphQLDate) => moment(date).format('YYYY-MM-DD');

const getScreenProperties = () => {
  // Segment auto creates columns for each property and tries to guess the correct type at first use. When the density
  // is 2 it creates a NUMBER column, when it is 2.75 it creates a FLOAT column. To avoid such ambiguity, we decided to
  // emit the density as string (rounded 2 second digit so it will always be a string
  const density = window.devicePixelRatio.toFixed(2);
  return {
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      density,
    },
  };
};

const getBrowserWindowProperties = () => {
  return {
    window: {
      height: window.innerHeight,
      width: window.innerWidth,
    },
  };
};
const useAnalyticsProvider = (debug?: boolean) => {
  const analyticsRef = useRef<IAnalyticsState>({
    state: AnalyticsState.NotAvailable,
  });

  useEffect(() => {
    const { analyticsConfig, syndicateId, syndicateRegion } =
      Syndicate.getData();

    const writeKey = analyticsConfig.segment?.writeKey;

    // KBT-1767: disable for screenshot capture feature
    const analyticsEnabled = IS_SCREENSHOT_MODE
      ? false
      : analyticsConfig.enabled;
    if (analyticsEnabled && !isNil(writeKey) && writeKey !== '') {
      analyticsRef.current = {
        state: AnalyticsState.Public,
        // (KBT-1377) following the instructions to configure custom domain
        // https://segment.com/docs/connections
        // /sources/catalog/libraries/website/javascript/custom-proxy/#npm-instructions
        browser: AnalyticsBrowser.load(
          {
            writeKey,
            cdnURL: 'https://cdn.analytics.kubit.ai',
          },
          {
            integrations: {
              'Segment.io': {
                apiHost: 'api.analytics.kubit.ai/v1',
                protocol: 'https',
              },
            },
          },
        ),
        partialCustomerAnalyticsInfo: {
          org_name: syndicateId,
          org_region: syndicateRegion,
        },
      };
    }
  }, []);

  const init = useCallback(
    (
      customerInfo: ICustomerInfoFieldsFragment,
      user: IUserFieldsFragment,
      userRoles: IRoleWithPermissionsFieldsFragment[],
    ) => {
      if (analyticsRef.current.state === AnalyticsState.Public) {
        const roles = userRoles.map((r) => r.name);
        analyticsRef.current = {
          state: AnalyticsState.LoggedIn,
          browser: analyticsRef.current.browser,
          customerAnalyticsInfo: {
            org_name:
              analyticsRef.current.partialCustomerAnalyticsInfo.org_name,
            org_region:
              analyticsRef.current.partialCustomerAnalyticsInfo.org_region,
            warehouse_name: customerInfo.warehouse,
            org_created_on_date: toIsoDate(customerInfo.createdAt),
          },
          userAnalyticsInfo: {
            user_email: user.email,
            user_role: roles,
            first_login_date: !isNil(user.createdAt)
              ? toIsoDate(user.createdAt)
              : null,
          },
        };

        analyticsRef.current.browser.identify(user.id).catch((e) => {
          console.error(`📊 Failed to identify user ${user}`, e);
        });
      }
    },
    [],
  );

  const track = useCallback(
    (event: IAnalyticsEventBase) => {
      const analytics = analyticsRef.current;
      if (analytics.state === AnalyticsState.NotAvailable) {
        return;
      }
      const { eventName, ...properties } = event;
      const browser = analytics.browser;
      const screenProps = getScreenProperties();
      const browserWindowProps = getBrowserWindowProperties();
      const eventProperties =
        analytics.state === AnalyticsState.LoggedIn
          ? {
              ...analytics.customerAnalyticsInfo,
              ...analytics.userAnalyticsInfo,
              ...screenProps,
              ...browserWindowProps,
              ...properties,
            }
          : {
              ...analytics.partialCustomerAnalyticsInfo,
              ...screenProps,
              ...browserWindowProps,
              ...properties,
            };

      if (debug) {
        console.info(`📊 ${eventName}`, eventProperties);
      }
      browser.track(eventName, eventProperties).catch((e) => {
        console.error(`📊 Failed to track event ${eventName}`, e);
      });

      // Duplicate Page Viewed event as Segment's .page() tracking
      // https://segment.com/docs/connections/spec/page/
      if (event.eventName === PAGE_VIEWED_EVENT) {
        const pageEvent = event as IAnalyticsEventPage;
        browser
          .page(pageEvent.object_type, pageEvent.object_name, eventProperties)
          .catch((e) => {
            console.error(`📊 Failed to track page ${eventName}`, e);
          });
      }
    },
    [debug],
  );

  return useMemo(
    () => ({
      init,
      track,
    }),
    [init, track],
  );
};

export type IAnalyticsProviderContext = ReturnType<typeof useAnalyticsProvider>;
export type IAnalyticsTrack = IAnalyticsProviderContext['track'];

export default useAnalyticsProvider;
